import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "@/components";
import { Button } from "@/components/Button/Button";

interface CampaignsDisableBlockedModalProps {
	isOpened: boolean;
	/** The campaigns that are still sending — the user has to pause these first. */
	campaignNames: string[];
	onClose: () => void;
}

/**
 * Why the "Enable campaigns on this list" toggle refused to switch off (PREC-3687 O4): at least one
 * campaign is still sending, and turning campaigns off must not silently strand its enrolled leads.
 *
 * The backend rejects the same `PATCH` on its own — this modal exists so the user is told *which*
 * campaigns to pause instead of watching a toggle spring back.
 */
export const CampaignsDisableBlockedModal: FunctionComponent<CampaignsDisableBlockedModalProps> = ({
	isOpened,
	campaignNames,
	onClose,
}) => {
	const { t } = useTranslation();
	const tb = (key: string) => t(`list.outreach.campaigns.disableBlocked.${key}`);

	return (
		<Modal
			footer={
				<div className="flex w-full justify-end">
					<div className="w-fit">
						<Button title={tb("gotIt")} onClick={onClose} />
					</div>
				</div>
			}
			handleClose={onClose}
			isOpened={isOpened}
			size="sm"
			title={tb("title")}
		>
			<p className="text-xs leading-5 text-text-meta">{tb("description")}</p>
			<ul className="mt-3 list-disc space-y-1 pl-4 text-sm font-medium text-text">
				{campaignNames.map((name) => (
					<li key={name}>{name}</li>
				))}
			</ul>
		</Modal>
	);
};
